// =============================================
// mapGenerationCore.ts — pure-function LLM logic per stage
// =============================================
// Ekstrakt logiki LLM z useMapGenerator.tsx (STOP-B planu auto-generate-maps).
//
// CEL: dac bulk runnerowi (autoGenerateMapRunner.ts) sciezke wywolania
// generowania (Macro / Seeds / Nodes batch / Entities / EAV / Relations)
// BEZ React state, BEZ toast, BEZ getPrompt-hook'a.
//
// PARITY: kazda funkcja core uzywa DOKLADNIE tych samych:
//   - generateXxx() z thematicMapService.ts (LLM call)
//   - deduplicationService (hashQuestion, checkDuplicate, checkSemanticDuplicates)
//   - entityNormalizer (mergeEntitiesByNormalization, normalizeEAVResults)
//   - normalizeNodeLanguage (validation)
//   - contentLogger startLogRun/endLogRun/logPublic
// co odpowiadajacy `run*` w useMapGenerator.tsx — tylko bez React-coupled wrapper.
//
// Hook `useMapGenerator` zostaje jako CIENKI wrapper:
//   - fetcha prompt przez getPrompt
//   - sprawdza worker mode (enqueueMapStep)
//   - przekazuje do core function
//   - setGenerating/setCurrentStep + toast wokol core call
//   - logRun (DB log) z metadata zwroconym z core
//
// =============================================

import { supabase } from "@/integrations/supabase/client";
import {
  generateMacro,
  generateSeeds,
  generateNodes,
  generateEntities,
  generateRelations,
  generateEAV,
  fillPromptTemplate,
} from "@/services/thematicMapService";
import {
  hashQuestion,
  checkDuplicate,
  checkSemanticDuplicates,
} from "@/services/deduplicationService";
import {
  mergeEntitiesByNormalization,
  normalizeEAVResults,
} from "@/services/entityNormalizer";
import { normalizeNodeLanguage } from "@/services/validation/reverseSerpValidator";
import {
  startLogRun,
  endLogRun,
  logPublic,
  logError,
} from "@/services/contentEngine/contentLogger";
import type {
  ThematicMap,
  ThematicSeed,
  ThematicNode,
  ThematicEntity,
  ThematicPrompt,
  PromptModel,
  LLMMacroOutput,
  LLMSeedOutput,
  LLMEntityOutput,
  LLMRelationOutput,
  LLMEAVOutput,
  NodePreview,
  BatchNodeResult,
} from "@/types/thematicMaps";

// =============================================
// Common types
// =============================================

/** Minimum needed from a prompt row to do the LLM call (matches ThematicPrompt subset). */
export type PromptInput = Pick<ThematicPrompt, "prompt_template" | "model">;

/** Minimum config from pipeline_config table. */
export interface StageConfigInput {
  batch_size: number;
  parallel_batches: number;
}

/** Result of a core stage call. Mirrors GenerationResult<T> z thematicMapService
 *  plus extras zeby hook mogl zalogowac do DB (filledPrompt + vars). */
export interface CoreResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  tokensInput: number;
  tokensOutput: number;
  modelUsed: string;
  /** Filled prompt template (dla logRun w hook). */
  filledPrompt: string;
  /** Vars passed do template (dla logRun w hook). */
  vars: Record<string, unknown>;
  /** Per-stage extras for telemetry/logging. */
  meta?: Record<string, unknown>;
}

// =============================================
// 1. Macro
// =============================================

export interface GenerateMacroArgs {
  map: ThematicMap;
  websiteName?: string;
  websiteUrl?: string;
  prompt: PromptInput;
}

export async function generateMacroAI(
  args: GenerateMacroArgs,
): Promise<CoreResult<LLMMacroOutput>> {
  const { map, websiteName, websiteUrl, prompt } = args;

  startLogRun(`macro-${map.id}`, {
    mapId: map.id,
    mapName: map.name,
    phase: "macro_generation",
  });
  logPublic("MACRO", `START — map: "${map.name}"`);
  const startTime = Date.now();

  const vars = {
    website_name: websiteName || "",
    website_url: websiteUrl || "",
    name: map.name,
  };
  const filledPrompt = fillPromptTemplate(prompt.prompt_template, vars);

  const result = await generateMacro(
    prompt.prompt_template,
    vars,
    prompt.model as PromptModel,
  );

  logPublic("MACRO", result.success ? "OK" : `FAILED: ${result.error}`);
  endLogRun({
    phase: "macro",
    success: result.success,
    duration_s: Math.round((Date.now() - startTime) / 1000),
  });

  return {
    success: result.success,
    data: result.data,
    error: result.error,
    tokensInput: result.tokensInput || 0,
    tokensOutput: result.tokensOutput || 0,
    modelUsed: result.modelUsed || prompt.model,
    filledPrompt,
    vars,
  };
}

// =============================================
// 2. Seeds
// =============================================

export interface GenerateSeedsArgs {
  map: ThematicMap;
  prompt: PromptInput;
}

export async function generateSeedsAI(
  args: GenerateSeedsArgs,
): Promise<CoreResult<LLMSeedOutput[]>> {
  const { map, prompt } = args;

  startLogRun(`seeds-${map.id}`, {
    mapId: map.id,
    mapName: map.name,
    phase: "seed_generation",
  });
  logPublic(
    "SEEDS",
    `START — macro: "${map.macro_statement?.slice(0, 60)}..."`,
  );
  const startTime = Date.now();

  const vars = {
    macro_statement: map.macro_statement,
    scope_in: (map.scope_in || []).join(", "),
    scope_out: (map.scope_out || []).join(", "),
    audience: map.audience || "",
    language: map.language || "pl",
  };
  const filledPrompt = fillPromptTemplate(prompt.prompt_template, vars);

  const result = await generateSeeds(
    prompt.prompt_template,
    vars,
    prompt.model as PromptModel,
  );

  logPublic(
    "SEEDS",
    result.success
      ? `OK — ${result.data?.length || 0} seeds`
      : `FAILED: ${result.error}`,
  );
  endLogRun({
    phase: "seeds",
    success: result.success,
    count: result.data?.length || 0,
    duration_s: Math.round((Date.now() - startTime) / 1000),
  });

  return {
    success: result.success,
    data: result.data,
    error: result.error,
    tokensInput: result.tokensInput || 0,
    tokensOutput: result.tokensOutput || 0,
    modelUsed: result.modelUsed || prompt.model,
    filledPrompt,
    vars,
  };
}

// =============================================
// 3. Nodes batch (growing dedup pool po seedach)
// =============================================

export interface GenerateNodesBatchArgs {
  map: ThematicMap;
  seedList: ThematicSeed[];
  existingNodes: ThematicNode[];
  nodePrompt: PromptInput;
  /** Opcjonalny prompt deduplication — uzywany dla semantic dedup po LLM call. */
  dedupPrompt?: PromptInput;
  config: StageConfigInput;
  onProgress?: (current: number, total: number, seedName: string) => void;
}

export async function generateNodesBatchAI(
  args: GenerateNodesBatchArgs,
): Promise<CoreResult<BatchNodeResult[]>> {
  const {
    map,
    seedList,
    existingNodes,
    nodePrompt,
    dedupPrompt,
    config,
    onProgress,
  } = args;

  const batchStartTime = Date.now();
  startLogRun(`batch-nodes-${map.id}`, {
    mapId: map.id,
    seedCount: seedList.length,
    phase: "batch_node_generation",
  });
  logPublic("BATCH_NODES", `START — ${seedList.length} seeds`);

  const results: BatchNodeResult[] = [];
  let totalTokensIn = 0;
  let totalTokensOut = 0;
  let modelUsed = nodePrompt.model;

  // Growing list for dedup: existing DB nodes + nodes generated in previous iterations
  const allExistingForCheck = existingNodes.map((n) => ({
    canonical_question_hash: n.canonical_question_hash,
    primary_entity_raw: n.primary_entity_raw,
    document_role: n.document_role,
    title: n.title,
    slug: n.slug,
  }));

  const SEED_PARALLEL = Math.max(1, config.parallel_batches || 1);

  for (let i = 0; i < seedList.length; i += SEED_PARALLEL) {
    const seedBatch = seedList.slice(i, i + SEED_PARALLEL);

    const batchPromises = seedBatch.map(async (seed, batchIdx) => {
      const seedIdx = i + batchIdx;
      onProgress?.(seedIdx + 1, seedList.length, seed.name);

      const startTime = Date.now();

      const existingContext = [
        ...existingNodes,
        ...allExistingForCheck.slice(existingNodes.length),
      ]
        .map((n) => `- ${n.title} (/${n.slug}) [${n.document_role}]`)
        .join("\n");

      const vars = {
        macro_statement: map.macro_statement,
        seed_name: seed.name,
        seed_slug: seed.slug,
        seed_intent_family: (seed.intent_family || []).join(", "),
        existing_nodes_in_map: existingContext || "(brak)",
        language: map.language || "pl",
        seed_type: seed.seed_type || "",
        scope_coverage: Array.isArray(seed.scope_coverage)
          ? seed.scope_coverage.join(", ")
          : "",
        other_seed_names: seedList
          .filter((s) => s.id !== seed.id)
          .map((s) => s.name)
          .join(", "),
      };

      const result = await generateNodes(
        nodePrompt.prompt_template,
        vars,
        nodePrompt.model as PromptModel,
      );

      return { seed, result, startTime, vars };
    });

    const batchResults = await Promise.all(batchPromises);

    for (const { seed, result } of batchResults) {
      if (!result.success) {
        // HOTFIX 2026-05-27: glosny log per-seed gdy nodes generation pada PO wyczerpaniu
        // 3 retries w claudeProvider.fetchWithRetry. Bez tego logu user widzi tylko zbiorcze
        // "DONE — X nodes (Y bledow)" bez nazwy seeda, ktory padl.
        logError(
          "BATCH_NODES",
          `Mapa "${map.name}" seed "${seed.name}" (id=${seed.id.slice(0, 8)}) — node_generation FAILED po retry: ${result.error}`,
        );
        results.push({
          seedId: seed.id,
          seedName: seed.name,
          nodes: [],
          error: result.error,
        });
        continue;
      }

      totalTokensIn += result.tokensInput || 0;
      totalTokensOut += result.tokensOutput || 0;
      if (result.modelUsed) modelUsed = result.modelUsed;

      // Normalize node language — split 2-intent, language consistency
      let normalizedNodes = result.data!;
      try {
        const rawPreviews: NodePreview[] = result.data!.map((node) => ({
          ...node,
          canonical_question_hash: hashQuestion(node.canonical_question),
          duplicate_level: "unique" as const,
        }));

        const normalized = await normalizeNodeLanguage(
          rawPreviews,
          map.language || "pl",
        );
        if (normalized.length > 0) {
          normalizedNodes = normalized.map((n) => ({
            title: n.title,
            slug: n.slug,
            document_role: n.document_role,
            primary_intent: n.primary_intent,
            primary_entity: n.primary_entity,
            canonical_question: n.canonical_question,
            required_entities: n.required_entities,
          }));
        }
      } catch {
        // Normalize failed — kontynuuj z raw nodes.
      }

      // 3-kolorowy preview z dedup (uwzglednia nodes z poprzednich iteracji)
      const previews: NodePreview[] = normalizedNodes.map((node) => {
        const hash = hashQuestion(node.canonical_question);
        const dupeCheck = checkDuplicate(
          node.canonical_question,
          node.primary_entity,
          node.document_role,
          allExistingForCheck,
        );

        return {
          ...node,
          canonical_question_hash: hash,
          duplicate_level: dupeCheck.level,
          duplicate_reason: dupeCheck.reason,
          duplicate_match_slug: dupeCheck.matchSlug,
        };
      });

      // Semantic dedup (LLM-based) — uruchom tylko jesli dedupPrompt podany
      if (
        dedupPrompt &&
        previews.filter((p) => p.duplicate_level === "unique").length > 1
      ) {
        try {
          await checkSemanticDuplicates(
            previews,
            dedupPrompt.prompt_template,
            dedupPrompt.model as PromptModel,
          );
        } catch {
          // Semantic dedup failed — kontynuuj z heuristic results.
        }
      }

      // Dodaj wygenerowane nodes do growing dedup pool
      for (const p of previews) {
        allExistingForCheck.push({
          canonical_question_hash: p.canonical_question_hash,
          primary_entity_raw: p.primary_entity,
          document_role: p.document_role,
          title: p.title,
          slug: p.slug,
        });
      }

      results.push({
        seedId: seed.id,
        seedName: seed.name,
        nodes: previews,
      });
    }
  }

  const totalNodes = results.reduce((sum, r) => sum + r.nodes.length, 0);
  const errors = results.filter((r) => r.error).length;
  const success = totalNodes > 0 || (seedList.length > 0 && errors === 0);

  logPublic(
    "BATCH_NODES",
    `DONE — ${totalNodes} nodes z ${seedList.length} seeds (${errors} bledow)`,
  );
  endLogRun({
    phase: "batch_nodes",
    total_nodes: totalNodes,
    errors,
    seeds: seedList.length,
    duration_s: Math.round((Date.now() - batchStartTime) / 1000),
  });

  return {
    success,
    data: results,
    error: errors > 0 && totalNodes === 0 ? "all_seeds_failed" : undefined,
    tokensInput: totalTokensIn,
    tokensOutput: totalTokensOut,
    modelUsed,
    filledPrompt: "(per-seed, batch)",
    vars: { seedCount: seedList.length, parallel: SEED_PARALLEL },
    meta: { totalNodes, errors, seedsProcessed: seedList.length },
  };
}

// =============================================
// 4. Entities (batch + dedup + merge + frequency filter)
// =============================================

export interface ExtractEntitiesArgs {
  map: ThematicMap;
  nodes: ThematicNode[];
  prompt: PromptInput;
  config: StageConfigInput;
  onProgress?: (msg: string) => void;
}

export async function extractEntitiesAI(
  args: ExtractEntitiesArgs,
): Promise<CoreResult<LLMEntityOutput[]>> {
  const { map, nodes, prompt, config, onProgress } = args;

  startLogRun(`entities-${map.id}`, {
    mapId: map.id,
    nodeCount: nodes.length,
    phase: "entity_extraction",
  });
  logPublic("ENTITIES", `START — ${nodes.length} nodes`);
  const startTime = Date.now();

  // Collect all unique raw entities
  const allEntities = new Set<string>();
  for (const n of nodes) {
    if (n.primary_entity_raw) allEntities.add(n.primary_entity_raw);
    for (const e of n.required_entities_raw || []) {
      allEntities.add(e);
    }
  }
  const entityList = Array.from(allEntities);

  // Zbierz scope_coverage ze wszystkich aktywnych seedow (anti-merge context)
  const { data: seedsForScope } = await (supabase as any)
    .from("thematic_seeds")
    .select("scope_coverage")
    .eq("map_id", map.id)
    .or("status.is.null,status.eq.active");

  const scopeEntities = [
    ...new Set(
      (seedsForScope || [])
        .flatMap((s: { scope_coverage?: string[] }) => s.scope_coverage || [])
        .map((e: string) => e.toLowerCase().trim())
        .filter(Boolean),
    ),
  ]
    .sort()
    .join(", ");

  logPublic(
    "ENTITIES",
    `Scope entities (anti-merge): ${scopeEntities || "(brak)"}`,
  );

  const ENTITY_BATCH_SIZE = config.batch_size;
  const PARALLEL_BATCHES = config.parallel_batches;

  const batches: string[][] = [];
  for (let i = 0; i < entityList.length; i += ENTITY_BATCH_SIZE) {
    batches.push(entityList.slice(i, i + ENTITY_BATCH_SIZE));
  }

  const allResults: LLMEntityOutput[] = [];
  let totalTokensIn = 0;
  let totalTokensOut = 0;
  let modelUsed = prompt.model;
  let firstError: string | undefined;

  for (
    let groupStart = 0;
    groupStart < batches.length;
    groupStart += PARALLEL_BATCHES
  ) {
    const group = batches.slice(groupStart, groupStart + PARALLEL_BATCHES);

    onProgress?.(
      `Batch ${groupStart + 1}-${Math.min(groupStart + group.length, batches.length)}/${batches.length} (${group.length} rownolegle)...`,
    );

    const groupResults = await Promise.all(
      group.map(async (batch) => {
        const batchStr = batch.join(", ");
        const vars = {
          entities_raw: batchStr,
          raw_entities: batchStr,
          macro_statement: map.macro_statement || "",
          language: map.language || "pl",
          scope_entities: scopeEntities,
        };
        return generateEntities(
          prompt.prompt_template,
          vars,
          prompt.model as PromptModel,
        );
      }),
    );

    for (const result of groupResults) {
      if (result.success && result.data) {
        allResults.push(...result.data);
        totalTokensIn += result.tokensInput || 0;
        totalTokensOut += result.tokensOutput || 0;
        if (result.modelUsed) modelUsed = result.modelUsed;
      } else if (!firstError && result.error) {
        firstError = result.error;
      }
    }

    // Single-batch all-fail → abort.
    const allFailed = groupResults.every((r) => !r.success);
    if (allFailed && batches.length === group.length) {
      const fullVars = {
        entities_raw: entityList.join(", "),
        raw_entities: entityList.join(", "),
        macro_statement: map.macro_statement || "",
        language: map.language || "pl",
        scope_entities: scopeEntities,
      };
      const filledPrompt = fillPromptTemplate(prompt.prompt_template, fullVars);
      logPublic(
        "ENTITIES",
        `FAILED — wszystkie batche w grupie padly: ${firstError || "unknown"}`,
      );
      endLogRun({
        phase: "entities",
        count: 0,
        batches: batches.length,
        duration_s: Math.round((Date.now() - startTime) / 1000),
      });
      return {
        success: false,
        data: [],
        error: firstError || "all_batches_failed",
        tokensInput: totalTokensIn,
        tokensOutput: totalTokensOut,
        modelUsed,
        filledPrompt,
        vars: fullVars,
        meta: { batches: batches.length },
      };
    }
  }

  // === POST-PROCESSING ENCJI ===

  // 1. Deduplicate by canonical_name (case-insensitive)
  const seen = new Set<string>();
  let deduped = allResults.filter((e) => {
    const key = e.canonical_name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // 2. Filtr: canonical_name < 3 znaki -> odrzuc
  deduped = deduped.filter((e) => e.canonical_name.length >= 3);

  // 3. Filtr: entity_type="attribute" z pustym description -> odrzuc
  deduped = deduped.filter(
    (e) =>
      !(
        e.entity_type === "attribute" &&
        (!e.description || e.description.trim().length < 5)
      ),
  );

  // 4. Merge aliasow: jesli canonical_name A jest w aliases B -> merge do B
  const mergedEntities: typeof deduped = [];
  for (const entity of deduped) {
    const existingByAlias = mergedEntities.find((m) =>
      m.aliases.some(
        (a) => a.toLowerCase() === entity.canonical_name.toLowerCase(),
      ),
    );
    if (existingByAlias) {
      const newAliases = [entity.canonical_name, ...entity.aliases].filter(
        (a) =>
          !existingByAlias.aliases.some(
            (ea) => ea.toLowerCase() === a.toLowerCase(),
          ) &&
          a.toLowerCase() !== existingByAlias.canonical_name.toLowerCase(),
      );
      existingByAlias.aliases.push(...newAliases);
    } else {
      mergedEntities.push(entity);
    }
  }
  deduped = mergedEntities;

  // 5. Filtr czestotliwosci: encje jednorazowe ktore NIE sa primary/required -> odrzuc
  const entityFreq = new Map<string, number>();
  const primaryRequiredSet = new Set<string>();
  for (const n of nodes) {
    if (n.primary_entity_raw) {
      primaryRequiredSet.add(n.primary_entity_raw.toLowerCase());
      entityFreq.set(
        n.primary_entity_raw.toLowerCase(),
        (entityFreq.get(n.primary_entity_raw.toLowerCase()) || 0) + 1,
      );
    }
    for (const req of n.required_entities_raw || []) {
      primaryRequiredSet.add(req.toLowerCase());
      entityFreq.set(
        req.toLowerCase(),
        (entityFreq.get(req.toLowerCase()) || 0) + 1,
      );
    }
  }
  for (const raw of entityList) {
    const lower = raw.toLowerCase();
    entityFreq.set(lower, (entityFreq.get(lower) || 0) + 1);
  }

  deduped = deduped.filter((e) => {
    const key = e.canonical_name.toLowerCase();
    const freq = entityFreq.get(key) || 0;
    const isPrimaryOrRequired =
      primaryRequiredSet.has(key) ||
      e.aliases.some((a) => primaryRequiredSet.has(a.toLowerCase()));
    return freq > 1 || isPrimaryOrRequired;
  });

  // 6. Fuzzy merge + alias cross-match + pseudo-entity filter
  deduped = mergeEntitiesByNormalization(deduped);

  // Log + return
  const fullVars = {
    entities_raw: entityList.join(", "),
    raw_entities: entityList.join(", "),
    macro_statement: map.macro_statement || "",
    language: map.language || "pl",
    scope_entities: scopeEntities,
  };
  const filledPrompt = fillPromptTemplate(prompt.prompt_template, fullVars);

  logPublic(
    "ENTITIES",
    `OK — ${deduped.length} encji (${batches.length} batchy)`,
  );
  endLogRun({
    phase: "entities",
    count: deduped.length,
    batches: batches.length,
    duration_s: Math.round((Date.now() - startTime) / 1000),
  });

  return {
    success: deduped.length > 0,
    data: deduped,
    error: deduped.length === 0 ? "no_entities_extracted" : undefined,
    tokensInput: totalTokensIn,
    tokensOutput: totalTokensOut,
    modelUsed,
    filledPrompt,
    vars: fullVars,
    meta: { batches: batches.length, rawCount: allResults.length },
  };
}

// =============================================
// 5. EAV (batch + normalize per-encja)
// =============================================

export interface ExtractEAVArgs {
  map: ThematicMap;
  entities: ThematicEntity[];
  nodes: ThematicNode[];
  prompt: PromptInput;
  config: StageConfigInput;
  onProgress?: (msg: string) => void;
}

export async function extractEAVAI(
  args: ExtractEAVArgs,
): Promise<CoreResult<LLMEAVOutput[]>> {
  const { map, entities, nodes, prompt, config, onProgress } = args;

  startLogRun(`eav-${map.id}`, {
    mapId: map.id,
    entityCount: entities.length,
    phase: "eav_extraction",
  });
  logPublic("EAV", `START — ${entities.length} encji`);
  const startTime = Date.now();

  const EAV_BATCH_SIZE = config.batch_size;
  const EAV_PARALLEL = config.parallel_batches;

  const batches: ThematicEntity[][] = [];
  for (let i = 0; i < entities.length; i += EAV_BATCH_SIZE) {
    batches.push(entities.slice(i, i + EAV_BATCH_SIZE));
  }

  const nodesJson = JSON.stringify(
    nodes.slice(0, 100).map((n) => ({
      title: n.title,
      slug: n.slug,
      document_role: n.document_role,
      primary_entity: n.primary_entity_raw,
      canonical_question: n.canonical_question,
    })),
  );

  const allResults: LLMEAVOutput[] = [];
  let totalTokensIn = 0;
  let totalTokensOut = 0;
  let modelUsed = prompt.model;
  let firstError: string | undefined;

  for (
    let groupStart = 0;
    groupStart < batches.length;
    groupStart += EAV_PARALLEL
  ) {
    const group = batches.slice(groupStart, groupStart + EAV_PARALLEL);

    onProgress?.(
      `Batch ${groupStart + 1}-${Math.min(groupStart + group.length, batches.length)}/${batches.length} (${group.length} rownolegle)...`,
    );

    const groupResults = await Promise.all(
      group.map(async (batch) => {
        const entitiesJson = JSON.stringify(
          batch.map((e) => ({
            canonical_name: e.canonical_name,
            entity_type: e.entity_type,
            description: e.description,
            aliases: e.aliases,
          })),
        );

        const vars = {
          map_name: map.name,
          macro_statement: map.macro_statement || "",
          scope_in: (map.scope_in || []).join(", "),
          scope_out: (map.scope_out || []).join(", "),
          entities_json: entitiesJson,
          nodes_json: nodesJson,
          language: map.language || "pl",
        };

        return generateEAV(
          prompt.prompt_template,
          vars,
          prompt.model as PromptModel,
        );
      }),
    );

    for (const result of groupResults) {
      if (result.success && result.data) {
        allResults.push(...result.data);
        totalTokensIn += result.tokensInput || 0;
        totalTokensOut += result.tokensOutput || 0;
        if (result.modelUsed) modelUsed = result.modelUsed;
      } else if (!firstError && result.error) {
        firstError = result.error;
      }
    }

    const allFailed = groupResults.every((r) => !r.success);
    if (allFailed && batches.length <= EAV_PARALLEL) {
      const fullVars = {
        map_name: map.name,
        macro_statement: map.macro_statement || "",
        entities_count: String(entities.length),
        nodes_count: String(nodes.length),
        language: map.language || "pl",
      };
      const filledPrompt = fillPromptTemplate(prompt.prompt_template, fullVars);
      logPublic("EAV", `FAILED: ${firstError || "all_failed"}`);
      endLogRun({
        phase: "eav",
        attributes: 0,
        entities: 0,
        duration_s: Math.round((Date.now() - startTime) / 1000),
      });
      return {
        success: false,
        data: [],
        error: firstError || "all_batches_failed",
        tokensInput: totalTokensIn,
        tokensOutput: totalTokensOut,
        modelUsed,
        filledPrompt,
        vars: fullVars,
      };
    }
  }

  // Post-processing — normalize + dedup per encja + confidence + limit
  const processedResults = normalizeEAVResults(allResults);
  const totalAttrs = processedResults.reduce(
    (sum, e) => sum + e.attributes.length,
    0,
  );

  const fullVars = {
    map_name: map.name,
    macro_statement: map.macro_statement || "",
    entities_count: String(entities.length),
    nodes_count: String(nodes.length),
    language: map.language || "pl",
  };
  const filledPrompt = fillPromptTemplate(prompt.prompt_template, fullVars);

  logPublic(
    "EAV",
    `OK — ${totalAttrs} atrybutow dla ${processedResults.length} encji`,
  );
  endLogRun({
    phase: "eav",
    attributes: totalAttrs,
    entities: processedResults.length,
    duration_s: Math.round((Date.now() - startTime) / 1000),
  });

  return {
    success: totalAttrs > 0,
    data: processedResults,
    error: totalAttrs === 0 ? "no_attributes_extracted" : undefined,
    tokensInput: totalTokensIn,
    tokensOutput: totalTokensOut,
    modelUsed,
    filledPrompt,
    vars: fullVars,
    meta: {
      batches: batches.length,
      rawCount: allResults.length,
      totalAttrs,
    },
  };
}

// =============================================
// 6. Relations (batch z seed-context fetch)
// =============================================

export interface GenerateRelationsArgs {
  map: ThematicMap;
  nodes: ThematicNode[];
  prompt: PromptInput;
  config: StageConfigInput;
  onProgress?: (msg: string) => void;
}

export async function generateRelationsAI(
  args: GenerateRelationsArgs,
): Promise<CoreResult<LLMRelationOutput[]>> {
  const { map, nodes, prompt, config, onProgress } = args;

  startLogRun(`relations-${map.id}`, {
    mapId: map.id,
    nodeCount: nodes.length,
    phase: "linking",
  });
  logPublic("RELATIONS", `START — ${nodes.length} nodes`);
  const startTime = Date.now();

  // Pobierz seed names (RAZ przed petlami) — pomijamy non-active seedy.
  const { data: seedsForLinking } = await (supabase as any)
    .from("thematic_seeds")
    .select("id, name")
    .eq("map_id", map.id)
    .or("status.is.null,status.eq.active");
  const seedNameMap = new Map(
    (seedsForLinking || []).map((s: { id: string; name: string }) => [
      s.id,
      s.name,
    ]),
  );

  const RELATION_BATCH_SIZE = config.batch_size;
  const REL_PARALLEL = config.parallel_batches;

  const batches: ThematicNode[][] = [];
  for (let i = 0; i < nodes.length; i += RELATION_BATCH_SIZE) {
    batches.push(nodes.slice(i, i + RELATION_BATCH_SIZE));
  }

  const allResults: LLMRelationOutput[] = [];
  let totalTokensIn = 0;
  let totalTokensOut = 0;
  let modelUsed = prompt.model;
  let firstError: string | undefined;

  for (
    let groupStart = 0;
    groupStart < batches.length;
    groupStart += REL_PARALLEL
  ) {
    const group = batches.slice(groupStart, groupStart + REL_PARALLEL);

    onProgress?.(
      `Batch ${groupStart + 1}-${Math.min(groupStart + group.length, batches.length)}/${batches.length} (${group.length} rownolegle)...`,
    );

    const groupResults = await Promise.all(
      group.map(async (batch) => {
        const nodesContext = batch
          .map((n) => {
            const seedName = seedNameMap.get(n.seed_id) || "unknown";
            return `- ${n.slug} "${n.title}" [${n.document_role}] entity="${n.primary_entity_raw}" seed="${seedName}"`;
          })
          .join("\n");

        const vars = {
          macro_statement: map.macro_statement,
          nodes_list: nodesContext,
          language: map.language || "pl",
        };

        return generateRelations(
          prompt.prompt_template,
          vars,
          prompt.model as PromptModel,
        );
      }),
    );

    for (const result of groupResults) {
      if (result.success && result.data) {
        allResults.push(...result.data);
        totalTokensIn += result.tokensInput || 0;
        totalTokensOut += result.tokensOutput || 0;
        if (result.modelUsed) modelUsed = result.modelUsed;
      } else if (!firstError && result.error) {
        firstError = result.error;
      }
    }

    const allFailed = groupResults.every((r) => !r.success);
    if (allFailed && batches.length <= REL_PARALLEL) {
      const fullNodesContext = nodes
        .map((n) => {
          const seedName = seedNameMap.get(n.seed_id) || "unknown";
          return `- ${n.slug} "${n.title}" [${n.document_role}] entity="${n.primary_entity_raw}" seed="${seedName}"`;
        })
        .join("\n");
      const fullVars = {
        macro_statement: map.macro_statement,
        nodes_list: fullNodesContext,
        language: map.language || "pl",
      };
      const filledPrompt = fillPromptTemplate(prompt.prompt_template, fullVars);
      logPublic("RELATIONS", `FAILED: ${firstError || "all_failed"}`);
      endLogRun({
        phase: "relations",
        count: 0,
        batches: batches.length,
        duration_s: Math.round((Date.now() - startTime) / 1000),
      });
      return {
        success: false,
        data: [],
        error: firstError || "all_batches_failed",
        tokensInput: totalTokensIn,
        tokensOutput: totalTokensOut,
        modelUsed,
        filledPrompt,
        vars: fullVars,
      };
    }
  }

  const fullNodesContext = nodes
    .map((n) => {
      const seedName = seedNameMap.get(n.seed_id) || "unknown";
      return `- ${n.slug} "${n.title}" [${n.document_role}] entity="${n.primary_entity_raw}" seed="${seedName}"`;
    })
    .join("\n");
  const fullVars = {
    macro_statement: map.macro_statement,
    nodes_list: fullNodesContext,
    language: map.language || "pl",
  };
  const filledPrompt = fillPromptTemplate(prompt.prompt_template, fullVars);

  logPublic(
    "RELATIONS",
    `OK — ${allResults.length} relacji (${batches.length} batchy)`,
  );
  endLogRun({
    phase: "relations",
    count: allResults.length,
    batches: batches.length,
    duration_s: Math.round((Date.now() - startTime) / 1000),
  });

  return {
    success: allResults.length > 0,
    data: allResults,
    error: allResults.length === 0 ? "no_relations_generated" : undefined,
    tokensInput: totalTokensIn,
    tokensOutput: totalTokensOut,
    modelUsed,
    filledPrompt,
    vars: fullVars,
    meta: { batches: batches.length },
  };
}
